import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, Building2, Calendar, ExternalLink } from "lucide-react";
import { useSite, usePublic } from "../../lib/site";
import { stripHtml } from "../../lib/i18n";
import { lazyImgs } from "../../lib/html";
import { Img } from "../../components/site/Common";
import { Seo, breadcrumbLd } from "../../components/site/Seo";
import NotFound, { Loading } from "./NotFound";

function Chip({ icon: Icon, children }) {
  if (!children) return null;
  return <span className="inline-flex items-center gap-2 text-sm text-s-muted"><Icon size={15} className="text-s-primary" />{children}</span>;
}

export default function TrainingDetail() {
  const { slug } = useParams();
  const { lang, t } = useSite();
  const { data: it, isLoading, isError } = usePublic(["training", slug], `/public/trainings/${slug}`);
  if (isLoading) return <Loading />;
  if (isError || !it) return <NotFound />;
  const title = t(it.title);
  const desc = t(it.description) || stripHtml(t(it.body)).slice(0, 155);
  const backTo = `${lang === "en" ? "/en" : ""}/egitimlerim`;
  const backLabel = lang === "tr" ? "Eğitimler" : "Trainings";
  const courseLd = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: title,
    description: desc,
    provider: { "@type": "Organization", name: t(it.org) || "Olgu Tansuğ" },
    ...(it.location ? { location: { "@type": "Place", name: it.location } } : {}),
    ...(it.date ? { startDate: it.date } : {}),
  };
  const bc = breadcrumbLd([{ name: backLabel, path: backTo }, { name: title, path: `${backTo}/${slug}` }]);
  return (
    <article data-testid="training-detail" className="pb-24">
      <Seo title={title} description={desc} image={it.certificate || it.cover} type="article" jsonLd={[courseLd, bc]} />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16">
        <Link to={backTo} className="inline-flex items-center gap-2 text-sm text-s-muted hover:text-s-primary transition-colors" data-testid="training-back-link"><ArrowLeft size={15} /> {backLabel}</Link>
      </div>
      <header className="max-w-3xl mx-auto px-5 sm:px-8 pt-8 pb-10 rise">
        {it.kind === "given" && <span className="inline-block px-3.5 py-1 rounded-full border border-site-border text-[11px] font-semibold uppercase tracking-[0.15em] text-s-primary mb-5">{lang === "tr" ? "Verdiğim Eğitim" : "Training I Give"}</span>}
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]" data-testid="training-title">{title}</h1>
        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-site-border pt-6">
          <Chip icon={Calendar}>{it.date}</Chip>
          <Chip icon={Building2}>{t(it.org)}</Chip>
          <Chip icon={MapPin}>{it.location}</Chip>
          <Chip icon={Clock}>{it.duration_text}</Chip>
          {t(it.level) && <span className="text-[11px] px-2.5 py-1 rounded-full border border-site-border text-s-muted">{t(it.level)}</span>}
        </div>
      </header>
      {it.certificate && (
        <div className="max-w-4xl mx-auto px-5 sm:px-8 mb-12">
          <div className="relative rounded-3xl border border-site-border bg-s-surface p-3 sm:p-4">
            <Img src={it.certificate} alt={`${title} sertifika`} data-testid="training-certificate-image" loading="eager" className="w-full rounded-2xl" />
          </div>
        </div>
      )}
      {t(it.body) && <div className="max-w-3xl mx-auto px-5 sm:px-8 prose-site" data-testid="training-body" dangerouslySetInnerHTML={{ __html: lazyImgs(t(it.body)) }} />}
      {!t(it.body) && t(it.description) && <p className="max-w-3xl mx-auto px-5 sm:px-8 text-lg text-s-text/80 leading-relaxed whitespace-pre-line">{t(it.description)}</p>}
      {it.link && (
        <div className="max-w-3xl mx-auto px-5 sm:px-8 mt-10">
          <a href={it.link} target="_blank" rel="noopener noreferrer" data-testid="training-external-link"
            className="inline-flex items-center gap-2 rounded-full bg-s-primary text-s-onprimary px-6 py-3 text-sm font-semibold hover:opacity-90 transition-opacity">
            {lang === "tr" ? "Sertifikayı / kaynağı aç" : "Open certificate / source"}<ExternalLink size={16} />
          </a>
        </div>
      )}
    </article>
  );
}
